import { ValidationError } from './validation.error';

export class GuardChain<T> {
  private errors: ValidationError[] = [];

  constructor(
    private value: T,
    private name: string,
  ) {}

  isNotNull(): this {
    if (this.value === null) {
      this.errors.push(new ValidationError(this.name, 'must not be null'));
    }
    return this;
  }

  isNotUndefined(): this {
    if (this.value === undefined) {
      this.errors.push(new ValidationError(this.name, 'must not be undefined'));
    }
    return this;
  }

  isNotNullOrUndefined(): this {
    this.isNotNull();
    this.isNotUndefined();
    return this;
  }

  isNotNaN(): this {
    if (Number.isNaN(this.value)) {
      this.errors.push(new ValidationError(this.name, 'must not be NaN'));
    }
    return this;
  }

  isPositiveNumber(): this {
    if (typeof this.value === 'number' && this.value <= 0) {
      this.errors.push(
        new ValidationError(this.name, 'must be a positive number'),
      );
    }
    return this;
  }

  isArray(): this {
    if (!Array.isArray(this.value)) {
      this.errors.push(new ValidationError(this.name, 'must be an array'));
    }
    return this;
  }

  isUrl(options?: { httpsOnly?: boolean }): this {
    if (typeof this.value !== 'string') {
      this.errors.push(new ValidationError(this.name, 'must be a string'));
      return this;
    }

    let url: URL;
    try {
      url = new URL(this.value);
    } catch {
      this.errors.push(new ValidationError(this.name, 'must be a valid URL'));
      return this;
    }

    // `new URL()` is permissive: `javascript:alert(1)` and `file:///x` parse with an
    // empty host, and `https:example.com` is silently normalised to `https://example.com`.
    // Require an explicit `scheme://host` so neither shape passes as a valid URL.
    if (
      url.host === '' ||
      !this.value.trim().toLowerCase().startsWith(`${url.protocol}//`)
    ) {
      this.errors.push(new ValidationError(this.name, 'must be a valid URL'));
      return this;
    }

    if (options?.httpsOnly === true && url.protocol !== 'https:') {
      this.errors.push(new ValidationError(this.name, 'must use https'));
    }

    return this;
  }

  isNotEmpty(): this {
    if (this.value === '') {
      this.errors.push(new ValidationError(this.name, 'must not be empty'));
    }

    if (Array.isArray(this.value) && this.value.length === 0) {
      this.errors.push(new ValidationError(this.name, 'must not be empty'));
    }

    return this;
  }

  assert(): void {
    if (this.errors.length > 0) {
      throw this.errors[0];
    }
  }
}
